"use client"

import { MobileMenuIcon } from "@/components/icons/mobile-menu-icon"
import { PumpFunOutlined } from "@/components/icons/pumpfun-outlined"
import { TgLogoOutlined } from "@/components/icons/tg-logo-outlined"
import { XLogoOutlined } from "@/components/icons/x-logo-outlined"
import { PUMP_FUN_LINK, TELEGRAM_LINK, X_LINK } from "@/lib/constants"
import {
    Drawer,
    DrawerContent,
    DrawerTitle,
    DrawerTrigger,
} from "@/components/ui/drawer"
import React from "react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { Routes } from "@/lib/routes"
import { ExternalLinkIcon } from "lucide-react"
import { usePathname } from "next/navigation"

const SocialLinks = [
    {
        href: TELEGRAM_LINK,
        ariaLabel: "Telegram",
        iconComponent: <TgLogoOutlined />,
    },
    {
        href: X_LINK,
        ariaLabel: "X (Twitter)",
        iconComponent: <XLogoOutlined />,
    },
    {
        href: PUMP_FUN_LINK,
        ariaLabel: "PumpFun",
        iconComponent: <PumpFunOutlined />,
    },
] satisfies Array<{
    href: string
    ariaLabel: string
    iconComponent: React.ReactNode
}>

export function MobileNav({ className }: { className?: string }) {
    const [open, setOpen] = React.useState(false)

    const pathname = usePathname()

    const onOpenChange = React.useCallback((open: boolean) => {
        setOpen(open)
    }, [])

    return (
        <Drawer open={open} onOpenChange={onOpenChange}>
            <DrawerTrigger asChild>
                <Button
                    variant="ghost"
                    className={cn(
                        "inline-flex",
                        className,
                        "cursor-pointer focus-visible:ring-0 focus-visible:ring-offset-0",
                        "size-12 [&_svg]:size-6",
                        "h-full bg-none",
                        open && "bg-white/5",
                    )}
                    size="icon"
                >
                    <MobileMenuIcon className="text-foreground size-7" />
                    <span className="sr-only">Toggle menu</span>
                </Button>
            </DrawerTrigger>
            <DrawerTitle className="sr-only">Page nav</DrawerTitle>
            <DrawerContent className="max-h-[80svh] p-0">
                <div className="overflow-auto p-6">
                    <div className="flex flex-col gap-4">
                        {[
                            Routes.HOME,
                            Routes.ROADMAP,
                            Routes.TOKENOMICS,
                            Routes.WHITEPAPER,
                            Routes.TEAM,
                        ].map(
                            (item: {
                                href: string
                                text: string
                                isExternal?: boolean
                            }) =>
                                item.href && (
                                    <a
                                        key={item.href}
                                        href={item.href}
                                        target={
                                            item.isExternal
                                                ? "_blank"
                                                : undefined
                                        }
                                        rel={
                                            item.isExternal
                                                ? "noopener noreferrer"
                                                : undefined
                                        }
                                        onClick={() => onOpenChange?.(false)}
                                        className={cn(
                                            "group flex gap-2 rounded-full px-0 py-1",
                                        )}
                                    >
                                        <span
                                            className={cn(
                                                "align-text-bottom text-xl tracking-wide underline-offset-4 group-hover:underline",
                                                pathname === item.href &&
                                                    "text-white underline",
                                            )}
                                        >
                                            {item.text.toUpperCase()}
                                        </span>
                                        {item.isExternal && (
                                            <ExternalLinkIcon />
                                        )}
                                    </a>
                                ),
                        )}
                        <div className="mt-6 flex justify-end gap-4 px-5">
                            {SocialLinks.filter(
                                (link) => link.iconComponent,
                            ).map((link) => (
                                <a
                                    key={link.href}
                                    href={link.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={link.ariaLabel}
                                >
                                    {link.iconComponent}
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </DrawerContent>
        </Drawer>
    )
}
